import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef } from 'react';
import styled, { css } from 'styled-components';
import { AnimationTimings, createAnimation } from '../../../Constants/Animation';
import { LinearGradient } from '../../../Constants/Colors';
import ModalContent from './ModalContent';
import Icon from '../../../Components/Content/Icon';
export var ModalWidths;
(function (ModalWidths) {
    ModalWidths["SMALL"] = "300px";
    ModalWidths["MEDIUM"] = "500px";
    ModalWidths["LARGE"] = "800px";
})(ModalWidths || (ModalWidths = {}));
export var DrawerDirection;
(function (DrawerDirection) {
    DrawerDirection["LEFT"] = "left";
    DrawerDirection["RIGHT"] = "right";
    DrawerDirection["TOP"] = "top";
    DrawerDirection["BOTTOM"] = "bottom";
})(DrawerDirection || (DrawerDirection = {}));
export default function Modal(props) {
    const modalWrapperRef = useRef(null);
    const onClickWrapper = props.onClickWrapper;
    const showClose = props.showClose === undefined ? true : props.showClose;
    const allowWrapperClickToClose = props.allowWrapperClickToClose === undefined ? true : props.allowWrapperClickToClose;
    const maxWidth = props.maxWidth ? props.maxWidth : '700px';
    const drawerMode = props.drawerMode === undefined ? false : props.drawerMode;
    const drawerDirection = props.drawerDirection ? props.drawerDirection : DrawerDirection.RIGHT;
    const cta = props.cta === undefined ? false : props.cta;
    // Only handle wrapper click if mouse down and up are both on the wrapper directly
    // Stops the modal from closing if the mouse is dragged from inside to outside of the modal
    const mouseDownRef = useRef(false);
    const handleMouseDown = (e) => {
        if (e.target === modalWrapperRef.current) {
            mouseDownRef.current = true;
        }
    };
    const handleMouseUp = (e) => {
        if (e.target === modalWrapperRef.current && mouseDownRef.current === true) {
            handleWrapperClick(e);
        }
        mouseDownRef.current = false;
    };
    const handleCloseClick = (e) => {
        e.stopPropagation();
        props.close();
    };
    const handleWrapperClick = (e) => {
        onClickWrapper?.(e);
        if (allowWrapperClickToClose === true) {
            handleCloseClick(e);
        }
    };
    return (_jsx(ModalWrapper, { ref: modalWrapperRef, onMouseDown: handleMouseDown, onMouseUp: handleMouseUp, "$drawerMode": drawerMode, "$drawerDirection": drawerDirection, className: "modal-wrapper", children: _jsxs(ModalContentContainer, { onClick: (e) => e.stopPropagation(), "$maxWidth": maxWidth, "$cta": cta, "$drawerMode": drawerMode, "$drawerDirection": drawerDirection, children: [showClose ? (_jsx(ModalClose, { onClick: handleCloseClick, children: _jsx(Icon, { name: "x" }) })) : null, props.children ? (props.children) : props.content ? (props.content) : (_jsx(ModalContent, { title: props.title, text: props.text, body: props.body, subtitle: props.subtitle, actions: props.actions, cta: cta }))] }) }));
}
const ModalWrapper = styled.div `
	position: fixed;
	z-index: 600;
	width: 100vw;
	height: 100vh;
	height: calc(var(--vh, 1vh) * 100);
	top: 0;
	left: 0;
	background-color: rgba(21, 21, 23, 0.6);
	display: flex;

	${({ $drawerMode, $drawerDirection }) => {
    let content = '';
    if ($drawerMode) {
        switch ($drawerDirection) {
            case DrawerDirection.RIGHT:
            case DrawerDirection.LEFT:
                content = `
						align-items: center;
					`;
                break;
            case DrawerDirection.TOP:
            case DrawerDirection.BOTTOM:
                content = `
						justify-content: center;
					`;
                break;
        }
        switch ($drawerDirection) {
            case DrawerDirection.RIGHT:
                content += `
						justify-content: flex-end;
					`;
                break;
            case DrawerDirection.LEFT:
                content += `
						justify-content: flex-start;
					`;
                break;
            case DrawerDirection.TOP:
                content += `
						align-items: flex-start;
					`;
                break;
            case DrawerDirection.BOTTOM:
                content += `
						align-items: flex-end;
					`;
                break;
        }
    }
    else {
        content = `
				justify-content: center;
				align-items: center;
			`;
    }
    return content;
}}

	@keyframes modal-bg-fade-in {
		0% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}

	${createAnimation({
    name: 'modal-bg-fade-in',
    duration: '0.5s',
    timing: AnimationTimings.entranceExpressive,
    iterations: '1',
})}
`;
const ModalContentContainer = styled.div `
	position: relative;
	background: ${(props) => props.$cta
    ? LinearGradient(135, props.theme.palette.brand1, props.theme.palette.warning)
    : props.theme.palette.shade6};
	opacity: 1;
	box-shadow: 0 0 40px rgb(0 0 0 / 38%);

	${({ $maxWidth, $drawerMode, $drawerDirection, theme }) => {
    let content;
    if ($drawerMode) {
        switch ($drawerDirection) {
            case DrawerDirection.RIGHT:
            case DrawerDirection.LEFT:
                content = css `
						height: 100%;
					`;
                break;
            case DrawerDirection.TOP:
            case DrawerDirection.BOTTOM:
                content = css `
						width: 100%;
					`;
                break;
        }
    }
    else {
        content = css `
				max-width: ${$maxWidth};
				max-height: 100vh;
				overflow-y: auto;
				${theme.scrollbars.skinny}

				@media (max-width: 700px) {
					max-width: min(${$maxWidth}, 95vw);
				}

				@media (max-width: 600px) {
					max-width: min(${$maxWidth}, 100vw);
					width: 100vw;
				}

				@media (max-height: 800px) {
					max-width: min(${$maxWidth}, 80vw);

					@media (max-width: 1300px) {
						max-width: min(${$maxWidth}, 90vw);
					}

					@media (max-width: 1200px) {
						max-width: min(${$maxWidth}, 95vw);
					}

					@media (max-width: 1000px) {
						max-width: min(${$maxWidth}, 100vw);
					}
				}
			`;
    }
    return content;
}}

	@keyframes modal-slide-in-right {
		0% {
			transform: translateX(50%);
		}
		100% {
			transform: translateX(0);
		}
	}

	@keyframes modal-slide-in-left {
		0% {
			transform: translateX(-50%);
		}
		100% {
			transform: translateX(0);
		}
	}

	@keyframes modal-slide-in-top {
		0% {
			transform: translateY(-50%);
		}
		100% {
			transform: translateY(0);
		}
	}

	@keyframes modal-slide-in-bottom {
		0% {
			transform: translateY(50%);
		}
		100% {
			transform: translateY(0);
		}
	}

	${({ $drawerMode, $drawerDirection }) => {
    let direction = 'bottom';
    if ($drawerMode) {
        switch ($drawerDirection) {
            case DrawerDirection.RIGHT:
                direction = 'right';
                break;
            case DrawerDirection.LEFT:
                direction = 'left';
                break;
            case DrawerDirection.TOP:
                direction = 'top';
                break;
            case DrawerDirection.BOTTOM:
                direction = 'bottom';
                break;
        }
    }
    return createAnimation({
        name: `modal-slide-in-${direction}`,
        duration: '0.3s',
        timing: AnimationTimings.entranceExpressive,
        iterations: '1',
    });
}}
`;
const ModalClose = styled.div `
	color: ${(props) => props.theme.schemes.primary.text};
	position: absolute;
	top: 14px;
	right: 14px;
	cursor: pointer;
	font-size: ${(props) => props.theme.typography.lg};
	z-index: 701;

	&:hover {
		color: ${(props) => props.theme.palette.accent2};
	}
`;
//# sourceMappingURL=Modal.js.map